import React, { Component } from 'react';
import { object, func, bool, array, string } from 'prop-types';
import styled from 'styled-components';

import { NotificationRender } from 'library/components/notification';
import * as JourneyMapForm from 'apps/admin/styles/components/styled-journeymap-form';
import { resolve } from 'library/helpers/path-resolver';
import AnswerChoiceRow from 'apps/admin/pages/questions/components/answer-choice-form-row';
import Query from 'apps/admin/util/query';
import { jsonToInputParams } from 'library/util/graph';

const StyledAnswerChoiceRow = styled.div`
  display: flex;
  align-items: center;
  font-weight: 300;
  margin: 0 auto;
  > input.button {
    max-width: 100px;
    text-align: center;
  }
  > input.fields {
    font-weight: 500;
    max-width: 600px;
    &:read-only {
      background-color: inherit;
      border: none;
    }
  }
`;

class AnswerChoicesForm extends Component {
  state = {
    loading: false,
    removeIds: new Set([]),
    updatedAnswerChoices: this.props.answerChoices,
    title: this.props.internalTitle,
    method: this.props.method,
  };

  static getDerivedStateFromProps = (props, state) => {
    if (state.updatedAnswerChoices.length == 0) {
      return {
        updatedAnswerChoices: props.answerChoices,
      };
    }
    return null;
  };

  submitButtonText = () => {
    this.props.answerChoices ? 'Update' : 'Create';
  };

  removeButtonText = (answerChoiceId) =>
    this.state.removeIds.has(answerChoiceId) ? 'Restore' : 'Remove';

  handleRemove = (answerChoiceId) => {
    let updated = new Set([...this.state.removeIds]);
    if (this.state.removeIds.has(answerChoiceId)) {
      updated.delete(answerChoiceId);
    } else {
      updated = updated.add(answerChoiceId);
    }
    this.setState({ removeIds: updated });
  };

  handleChange = (event) => this.setState({ title: event.target.value });

  addAnswerChoice = () => {
    let updated = this.state.updatedAnswerChoices;
    updated.unshift({
      id: 'answer-choice-' + (this.state.updatedAnswerChoices.length + 1),
      key: '',
      value: '',
      order: this.state.updatedAnswerChoices.length + 1,
      internal_title: this.state.title,
    });
    this.setState({ updatedAnswerChoices: updated });
  };

  createAnswerChoiceSet = async () => {
    this.setState({ loading: true });
    try {
      const queryParams = {};
      const answer_choice_data = this.state.updatedAnswerChoices.filter(
        (choice) => !this.state.removeIds.has(choice.id)
      );

      if (this.state.title) {
        queryParams.internal_title = this.state.title;
      }

      if (answer_choice_data) {
        queryParams.answer_choice_data = JSON.stringify(answer_choice_data);
      }

      const result = await Query(`
        mutation {
          create_answer_choices(${jsonToInputParams(queryParams)}) {
            success
            errors
            answer_choices {
              id
            }
          }
        }
      `);

      if (resolve(result, 'data/create_answer_choices/errors', false)) {
        NotificationRender(
          'success',
          'Answer choices were successfully created!'
        );
      } else {
        console.error(
          resolve(result, 'data/create_answer_choices/errors', false)
        );
        NotificationRender('error', 'Something went wrong!');
      }
    } catch (e) {
      console.error(e);
      NotificationRender('error', 'Something went wrong!');
    }
    this.setState({ loading: false });
    this.props.toggleActive();
  };

  updateAnswerChoiceSet = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({ loading: true });
    try {
      const queryParams = {};
      const answer_choice_data = this.state.updatedAnswerChoices.filter(
        (choice) => !this.state.removeIds.has(choice.id)
      );

      if (this.props.internalTitle) {
        queryParams.internal_title = this.props.internalTitle;
      }

      if (this.state.title !== this.props.internalTitle) {
        queryParams.new_internal_title = this.state.title;
      }

      if (answer_choice_data) {
        queryParams.answer_choice_data = JSON.stringify(answer_choice_data);
      }

      const result = await Query(`
        mutation {
          update_answer_choices(${jsonToInputParams(queryParams)}) {
            success
            errors
            answer_choices {
              id
            }
          }
        }
      `);

      if (resolve(result, 'data/update_answer_choices/errors', false)) {
        NotificationRender(
          'success',
          'Answer choices were successfully updated!'
        );
      } else {
        console.error(
          resolve(result, 'data/update_answer_choices/errors', false)
        );
        NotificationRender('error', 'Something went wrong!');
      }
    } catch (e) {
      console.error(e);
      NotificationRender('error', 'Something went wrong!');
    }
    this.setState({ loading: false });
    this.props.toggleActive();
  };

  mapAnswerChoices = (answerChoice) => (
    <StyledAnswerChoiceRow
      key={answerChoice.id}
      style={{
        opacity: this.state.removeIds.has(answerChoice.id) ? '.6' : '1',
      }}
    >
      <AnswerChoiceRow answerChoice={answerChoice} />
      <input
        className="button"
        value={this.removeButtonText(answerChoice.id)}
        onClick={() => this.handleRemove(answerChoice.id)}
        readOnly={true}
      />
    </StyledAnswerChoiceRow>
  );

  render = () => (
    <JourneyMapForm.StyledForm
      onSubmit={
        this.state.method == 'edit'
          ? this.updateAnswerChoiceSet
          : this.createAnswerChoiceSet
      }
    >
      <JourneyMapForm.StyledFormContainer>
        <JourneyMapForm.NameLabel>
          Internal Title
          <JourneyMapForm.NameInput
            defaultValue={this.props.internalTitle}
            placeholder="Internal Title"
            name="internal_title"
            onChange={this.handleChange}
          />
        </JourneyMapForm.NameLabel>
        <JourneyMapForm.NameLabel>Answer Choices</JourneyMapForm.NameLabel>
        <JourneyMapForm.SubmitButtons>
          <input
            className="button"
            value="Add New"
            onClick={this.addAnswerChoice}
            readOnly={true}
          />
        </JourneyMapForm.SubmitButtons>
        {this.state.updatedAnswerChoices &&
          this.state.updatedAnswerChoices.map(this.mapAnswerChoices)}
      </JourneyMapForm.StyledFormContainer>
      <JourneyMapForm.FooterRow>
        <JourneyMapForm.SubmitButtons>
          <input
            className="button"
            value="Cancel"
            onClick={this.props.handleCancel}
            readOnly={true}
          />
          <input
            type="submit"
            className="button"
            defaultValue={this.submitButtonText}
            onSubmit={
              this.state.method == 'edit'
                ? this.updateAnswerChoiceSet
                : this.createAnswerChoiceSet
            }
            disabled={this.state.loading}
            readOnly={true}
          />
        </JourneyMapForm.SubmitButtons>
      </JourneyMapForm.FooterRow>
    </JourneyMapForm.StyledForm>
  );
}

AnswerChoicesForm.propTypes = {
  entity: object,
  toggleActive: func,
  handleCancel: func,
  reload_graph: func,
  handleChange: func,
  internalTitle: string,
  answerChoices: array,
  loading: bool,
  method: string,
};

export default AnswerChoicesForm;
